/**
 * Crash reporting via Sentry (web only)
 * https://docs.sentry.io/platforms/javascript/guides/react/
 */
import { Platform } from "react-native"
import * as Sentry from "@sentry/react"

import { downgradeExpectedThrow } from "./sentryExpectedThrows"

/**
 * Initialize Sentry — call once at app startup (web only)
 */
export const initCrashReporting = () => {
  if (Platform.OS !== "web") return

  Sentry.init({
    dsn: "https://57631d64c7e4da4441c9f236a7284201@o4511021108101121.ingest.de.sentry.io/4511021134839888",
    environment: __DEV__ ? "development" : "production",
    release: process.env.EXPO_PUBLIC_RELEASE_SHA || "dev",
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    enabled: !__DEV__,
    ignoreErrors: [
      "Prohibited read from data layer variable",
      // Belt-and-braces backstop: only catches errors bubbling to Sentry's global
      // handler (uncaught throws, unhandled rejections). Explicit captureException
      // call-sites filter NETWORK_ERROR via isTransientNetworkError before this regex
      // ever runs. Keeps unhandled future cellular drops from polluting Sentry.
      /NETWORK_ERROR/i,
      // B2C track-page uses tracking-token auth, not Firebase Auth. "Login
      // erforderlich" / auth-related Firebase HttpsErrors on the /track/* routes
      // are expected when a token is expired or the user re-visits a stale link.
      // These bubble as unhandled rejections from the global error handler rather
      // than explicit captureException calls (WEB-C/E/J/K).
      /Login erforderlich/i,
      /auth\/.*token/i,
    ],
    beforeSend(event, hint) {
      // 1) Drop outright the B2C track-page reschedule/cancel token-expiry error.
      // The B2C tracking callables (rescheduleB2cOrder/cancelB2cOrder) rewrap an
      // unauthenticated Firebase HttpsError into this exact German message; it's a
      // stale-tracking-link condition, not a bug — the user just needs a fresh link.
      // Match the specific rewrapped message, NOT every unauthenticated FirebaseError:
      // B2B callables (e.g. getFleetOrders) also captureException on unauthenticated,
      // and those must still reach Sentry to surface real auth regressions.
      const exceptionValues = event.exception?.values ?? []
      for (const ex of exceptionValues) {
        const msg = ex.value ?? ""
        if (msg.includes("Ungültiger oder abgelaufener Tracking-Link")) {
          return null
        }
      }
      // 2) Downgrade — never drop — the remaining expected backend callable
      // rejections (auth + validation HttpsErrors the backend already records at
      // WARN per ADR-0020) so the client copy doesn't surface as a client-side
      // ERROR. Kept as WARN so volume spikes stay detectable. See PLA-178.
      return downgradeExpectedThrow(event, hint)
    },
  })

  // Anonymous, session-scoped id so Sentry issues report distinct affected
  // sessions instead of "Users: 0". No PII — see getOrCreateSessionId(). PLA-633.
  Sentry.setUser({ id: getOrCreateSessionId() })
}

const SESSION_ID_STORAGE_KEY = "carassist_sentry_session_id"

// Fallback used when sessionStorage is unavailable (native — no DOM) or
// storage access throws (private browsing, quota, disabled storage). Keeps
// the id stable for the lifetime of this JS session even without storage.
let inMemorySessionId: string | undefined

const generateSessionUuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch {
    // Fall through to the manual fallback below.
  }
  // Non-cryptographic fallback — sufficient for a correlation id that is
  // never used to identify a real person.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === "x" ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

/**
 * Anonymous, session-scoped id used only to correlate Sentry events with a
 * browser session — never a person. Session-scoped is deliberate: it lets
 * Sentry count distinct affected sessions without a durable cross-visit
 * identifier. No PII, ever.
 *
 * Must never throw — storage access failures fall back to an in-memory id so
 * a broken/blocked sessionStorage can never break app boot. Also safe on
 * native, where there is no global `sessionStorage` at all (this file is
 * shared with the React Native codebase; only the web branch calls this).
 */
export const getOrCreateSessionId = (): string => {
  try {
    // `typeof` is required here — referencing the bare `sessionStorage`
    // identifier directly would throw a ReferenceError on native, where it
    // isn't declared as a global at all. It must also stay INSIDE the try:
    // browsers expose `sessionStorage` through an accessor, and in an opaque
    // or sandboxed origin (or with storage access blocked) that accessor
    // throws a SecurityError — `typeof` still invokes it, so an unguarded
    // probe would take the whole web boot down with it.
    if (typeof sessionStorage === "undefined") {
      return (inMemorySessionId ??= generateSessionUuid())
    }

    const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY)
    if (existing) return existing

    const created = generateSessionUuid()
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, created)
    return created
  } catch {
    // Storage access threw (private browsing, quota, disabled storage,
    // sandboxed origin, ...).
    return (inMemorySessionId ??= generateSessionUuid())
  }
}

/**
 * Error classifications used to sort errors on error reporting services.
 */
export enum ErrorType {
  /**
   * An error that would normally cause a red screen in dev
   * and force the user to sign out and restart.
   */
  FATAL = "Fatal",
  /**
   * An error caught by try/catch where defined using Reactotron.tron.error.
   */
  HANDLED = "Handled",
}

/**
 * Manually report a handled error.
 */
export const reportCrash = (error: Error, type: ErrorType = ErrorType.FATAL) => {
  if (__DEV__) {
    const _message = error.message || "Unknown"
    // Development error logging handled by Reactotron
  } else {
    Sentry.captureException(error, {
      tags: { errorType: type },
    })
  }
}
